import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  makeStyles
} from '@material-ui/core';
import {
  HeaderCell,
  SearchBar,
  SecondaryAlert,
  Table,
  TableBody,
  TableHead,
  TertiaryButton,
  SecondaryButton
} from 'kagr-ui';
import * as React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useSegments } from '../../../hooks/useSegments';
import Paths from '../../../models/enums/paths';
import { SegmentFilters, SegmentFiltersAdmin, SegmentOverviewResult } from '../../../models/segments';
import { TableSortState } from '../../../models/tableSortState';
import { DateTimeFormatter } from '../../../shared/date-format';
import Pagination from '../../pagination/pagination';
import * as styles from '../segments.module.scss';
import SharingCell from './sharing-cell';
import { MoreVertSharp, LinkSharp } from '@material-ui/icons';
import { useSegmentDelete } from '../../../hooks/useSegmentDelete';
import useTimedAlerts from '../../../hooks/useTimedAlerts';
import { AppContext, AppProviderDispatch } from '../../../shared/app-context';
import SegmentationPermission from '../../../models/enums/segmentationPermission';
import useUser from '../../../hooks/useUser';
import { datadogRum } from '@datadog/browser-rum';

const useStyles = makeStyles((theme) => ({
  dialog: {
    [theme.breakpoints.up('sm')]: {
      '& .MuiDialog-paperWidthSm': {
        width: '95rem',
        maxWidth: '95rem'
      }
    }
  },
  dialogContent: {
    padding: 0
  },
  dialogActions: {
    justifyContent: 'space-between',
    height: 57,
    padding: '0 24px'
  },
  divider: {
    height: '25px',
    borderRight: '1px solid #979797',
    margin: '5px 0'
  },
  deleteMessage: {
    position: 'absolute',
    top: '20px',
    left: '45%'
  },
  linkCell: {
    '& td,th': {
      '&:first-child': {
        paddingLeft: 0,
        paddingRight: 5,
        textAlign: 'center'
      }
    }
  },
  noResultsMessage: {
    paddingLeft: 38
  },
  linkIconAlign: {
    paddingTop: 5
  }
}));

interface Props {
  columnHeaders: {
    title: string;
    orderField?: string;
  }[];
  initialSortState: TableSortState;
  activeFilters: SegmentFilters | SegmentFiltersAdmin;
}

const SegmentsTable = ({ columnHeaders, initialSortState, activeFilters }: Props) => {
  const overrides = useStyles();
  const [sort, setSort] = React.useState<TableSortState>(initialSortState);
  const [searchValue, setSearchValue] = React.useState<string>();
  const [activeSearchTerm, setActiveSearchTerm] = React.useState<string>();
  const segmentsQuery = useSegments(sort, activeSearchTerm, activeFilters);
  const [deleteSegment, setDeleteSegment] = React.useState<SegmentOverviewResult | null>(null);
  const segmentDelete = useSegmentDelete();
  const { successMessage, setSuccessMessage } = useTimedAlerts();
  const appState = React.useContext<AppProviderDispatch>(AppContext);
  const { getCurrentUser } = useUser();
  const [segmentationPermission, setSegmentationPermission] = React.useState<SegmentationPermission>(0);
  const userName = React.useRef<string>('');

  React.useEffect(() => {
    void checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      userName.current = `${currentUser.firstName} ${currentUser.lastName}`;
      const assignedSegmentation = currentUser.assignedSegmentation.find(
        (as) => as.clientId === appState.state.clientId
      );
      setSegmentationPermission(assignedSegmentation.segmentationPermission);
    } catch (error) {
      setSegmentationPermission(0);
    }
  };

  const userInterface = window.env.USER_INTERFACE ?? 'default';

  function reorderHandler(column: string, direction: 'asc' | 'desc') {
    setSort((state) => ({ ...state, orderColumn: column, orderDirection: direction }));
  }

  return (
    <div className={styles.content}>
      <div className={styles.tableHeader}>
        <div className={styles.titleContainer}>
          <h2 className={styles.tableTitle}>Team Segments</h2>
          {segmentsQuery.isFetching && !segmentsQuery.isLoading && <CircularProgress size="16px" />}
        </div>
        <SearchBar
          value={searchValue}
          updateValue={setSearchValue}
          placeholder="Search by Name"
          submitHandler={() => setActiveSearchTerm(searchValue)}
        />
      </div>
      {segmentsQuery.isLoading && !segmentsQuery.data && (
        <div className={styles.loading}>
          <CircularProgress />
        </div>
      )}
      {segmentsQuery.isError && (
        <div className={styles.error}>
          <SecondaryAlert type="error" message={`Failed to fetch results: ${segmentsQuery.error.message}`} />
        </div>
      )}
      {segmentsQuery.data && (
        <div className={styles.tableWrapper}>
          <Table
            className={styles.table}
            loading={segmentsQuery.isPreviousData && segmentsQuery.isFetching}
            results={segmentsQuery.data.results}
          >
            <TableHead className={overrides.linkCell}>
              <HeaderCell title="" />
              {columnHeaders.map((header) => (
                <HeaderCell
                  key={header.title}
                  title={header.title}
                  orderField={header.orderField ? header.orderField : null}
                  orderColumn={header.orderField ? sort.orderColumn : null}
                  orderDirection={header.orderField ? sort.orderDirection : null}
                  reorderHandler={header.orderField ? reorderHandler : null}
                />
              ))}
              {userInterface !== 'management' && <HeaderCell title="" />}
            </TableHead>
            <TableBody className={overrides.linkCell}>
              {segmentsQuery.data.totalItems === 0 && (
                <tr>
                  <td colSpan={columnHeaders.length}>
                    <span className={overrides.noResultsMessage}>No results found</span>
                  </td>
                </tr>
              )}
              {segmentsQuery.data.results.map((data) => (
                <tr key={`${data.id}`}>
                  <td>
                    {data.isDependency && (
                      <div className={overrides.linkIconAlign}>
                        <LinkSharp />
                      </div>
                    )}
                  </td>
                  {columnHeaders.find((header) => header.title == 'Team') !== undefined && <td>{data.clientName}</td>}
                  <td>
                    <Link
                      to={{
                        pathname: Paths.Segment.replace(':segmentId', String(data.id)),
                        state: { isEditing: false }
                      }}
                      className={styles.tableLink}
                      onClick={() => {
                        datadogRum.addUserAction('view-segment', {
                          user_action_details: {
                            segment_id: data.id,
                            segment_name: data.name,
                            is_segment_shared: data.isShared ? 'Shared' : 'Not Shared',
                            user_name: userName.current
                          }
                        });
                      }}
                    >
                      {data.name}
                    </Link>
                  </td>
                  <td className={styles.lastModifiedColumn}>
                    {DateTimeFormatter.format(new Date(data.lastModifiedDate))}
                  </td>
                  <td>{data.description}</td>
                  <td>{data.totalCustomers.toLocaleString()}</td>
                  <td>{data.createdByName}</td>
                  <SharingCell segment={data} />
                  {userInterface !== 'management' && (
                    <SegmentOptions
                      segment={data}
                      segmentationPermission={segmentationPermission}
                      deleteModal={(segment: SegmentOverviewResult) => setDeleteSegment(segment)}
                      userName={userName.current}
                    />
                  )}
                </tr>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {segmentsQuery.data && (
        <div className={styles.paginationFooter}>
          <Pagination
            totalPages={Math.ceil(segmentsQuery.data.totalItems / sort.pageSize)}
            pageSize={sort.pageSize}
            currentPage={sort.page}
            disabled={segmentsQuery.isPreviousData && segmentsQuery.isFetching}
            handlePageChange={(page) => setSort((state) => ({ ...state, page }))}
            handlePageSizeChange={(pageSize) => setSort((state) => ({ ...state, pageSize }))}
          />
        </div>
      )}
      <Dialog open={!!deleteSegment}>
        <DialogTitle disableTypography>Delete Segment</DialogTitle>
        <DialogContent>Are you sure you want to delete this segment?</DialogContent>
        <DialogActions className={overrides.dialogActions}>
          <TertiaryButton text="Cancel" title="Cancel" component="button" handleClick={() => setDeleteSegment(null)} />
          <SecondaryButton
            text="Delete"
            title="Delete"
            handleClick={() => {
              segmentDelete.mutate(
                {
                  segmentId: deleteSegment.id
                },
                {
                  onSuccess() {
                    datadogRum.addUserAction('delete-segment', {
                      user_action_details: {
                        segment_id: deleteSegment.id,
                        segment_name: deleteSegment.name,
                        is_segment_shared: deleteSegment.isShared ? 'Shared' : 'Not Shared',
                        user_name: userName.current
                      }
                    });
                    segmentsQuery.refetch();
                    setSuccessMessage('Segment deleted');
                  },
                  onSettled() {
                    setDeleteSegment(null);
                  }
                }
              );
            }}
          />
        </DialogActions>
      </Dialog>
      {successMessage && (
        <div className={overrides.deleteMessage}>
          <SecondaryAlert type="success" message={successMessage} />
        </div>
      )}
    </div>
  );
};

type SegmentOptionsProps = {
  segment: SegmentOverviewResult;
  segmentationPermission: SegmentationPermission;
  deleteModal: (segment: SegmentOverviewResult) => void;
  userName: string;
};

const SegmentOptions = ({ segment, segmentationPermission, deleteModal, userName }: SegmentOptionsProps) => {
  const history = useHistory();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <td>
      <IconButton aria-controls="segment-options-menu" aria-haspopup="true" onClick={handleClick}>
        <MoreVertSharp color="secondary" />
      </IconButton>
      <Menu id="segment-options-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            handleClose();
            datadogRum.addUserAction('clone-segment', {
              user_action_details: {
                segment_id: segment.id,
                segment_name: segment.name,
                is_segment_shared: segment.isShared ? 'Shared' : 'Not Shared',
                user_name: userName
              }
            });
            history.push(`${Paths.NewSegment}?clonedId=${segment.id}`);
          }}
        >
          Clone
        </MenuItem>
        {(segment.isEditable || segmentationPermission === SegmentationPermission.Power) && (
          <MenuItem
            onClick={() => {
              handleClose();
              deleteModal(segment);
            }}
          >
            Delete
          </MenuItem>
        )}
        {(segment.isEditable || segmentationPermission === SegmentationPermission.Power) && (
          <MenuItem
            onClick={() => {
              handleClose();
              history.push(`${Paths.Segment.replace(':segmentId', String(segment.id))}`, { isEditing: true });
            }}
          >
            Edit
          </MenuItem>
        )}
      </Menu>
    </td>
  );
};

export default SegmentsTable;
